import React, { useCallback, useEffect, useReducer, useState } from 'react'
import Modal from 'react-modal'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import classNames from 'classnames'

import { fetchKBSections, deleteKB, updateKB } from 'Actions/kb'
import { getKBSections, getUpdatingState } from 'Selectors/kb'
import loader from 'Assets/images/three-dots.svg?url'
import { SectionsList } from './SectionsList'

function reducer(state, action) {
  switch (action.type) {
    case 'SET_KB_NAME':
      return {
        ...state,
        kbName: action.payload
      }

    case 'SET_IGNORED_SECTIONS':
      return {
        ...state,
        ignoredSections: action.payload
      }

    case 'SET_KB_SECTIONS': {
      const parentSections = _.filter(action.payload, { parent_id: null })
      return {
        ...state,
        kbSections: action.payload,
        parentSections
      }
    }
  }
}

const SettingsModal = (props) => {
  const dispatch = useDispatch()
  const { kb, hide } = props
  const [showDeleteConfirmation, setDeleteConfirmation] = useState(false)
  const onClose = useCallback(hide, [hide])
  const kbId = kb.id
  const initialIgnoredSections = kb.ignored_sections.map((ignored_section) => ignored_section.id)
  const initialKbSections = useSelector(getKBSections)
  const updating = useSelector(getUpdatingState)
  const [isNameValid, setNameValidity] = useState(true)

  const initialState = {
    kbSections: [],
    kbName: kb.name,
    ignoredSections: initialIgnoredSections,
    parentSections: []
  }

  const [state, setState] = useReducer(reducer, initialState)

  useEffect(() => {
    setState({ type: 'SET_KB_SECTIONS', payload: initialKbSections })
  }, [initialKbSections])

  useEffect(() => {
    dispatch(fetchKBSections({ kbId }))
  }, [dispatch, kbId])

  const onDeleteKB = useCallback(() => {
    dispatch(deleteKB({ kbId }))
    setDeleteConfirmation(false)
  }, [dispatch, kbId])

  const handleNameChange = (event) => {
    validateName(event)
    setState({ type: 'SET_KB_NAME', payload: event.target.value })
  }

  const validateName = (event) => {
    if (event.target.value.length < 3) {
      setNameValidity(false)
    } else {
      setNameValidity(true)
    }
  }

  const onSave = useCallback(() => {
    dispatch(updateKB({ kbId, name: state.kbName, ignore_sections: state.ignoredSections }))
  }, [dispatch, state, kbId])

  return (
    <React.Fragment>
      <Modal className='modal-slideover' isOpen={props.open} ariaHideApp={false} onRequestClose={onClose}>
        <div className='modal-header'>
          <div className='modal-close' onClick={onClose}>
            &times;
          </div>
          Edit Knowledge Base
        </div>

        <div className='modal-body'>
          <fieldset>
            <label htmlFor='kb-name'>Name</label>
            <input
              className='text-input'
              id='kb-name'
              value={state.kbName}
              onChange={handleNameChange}
              onBlur={validateName}
              type='text'
            />
            {!isNameValid && <small className='text-danger'>Please enter a valid name</small>}
          </fieldset>

          <fieldset>
            <label>Ignored sections/groups</label>
            <div className='sections-container'>
              <SectionsList
                sections={state.parentSections}
                kbSections={state.kbSections}
                ignoredSections={state.ignoredSections}
                setState={setState}
              />
              {state.kbSections.length === 0 && kb.sections_count && (
                <div className='loading-container'>
                  <div className='empty-message'>Loading sections. This may take a while...</div>
                </div>
              )}

              {state.kbSections.length === 0 && !kb.sections_count && (
                <div className='loading-container'>
                  <div className='empty-message'>No sections found</div>
                </div>
              )}
            </div>
          </fieldset>
        </div>

        {!updating && (
          <div className='modal-footer'>
            {!showDeleteConfirmation && (
              <React.Fragment>
                <button className={classNames('button-primary', { disabled: !isNameValid })} onClick={onSave}>
                  Save
                </button>
                <button className='button-link' onClick={onClose}>
                  Cancel
                </button>
                <button className='button-link text-danger ml-auto' onClick={() => setDeleteConfirmation(true)}>
                  Delete
                </button>
              </React.Fragment>
            )}

            {showDeleteConfirmation && (
              <React.Fragment>
                Are you sure?
                <div className='ml-auto'>
                  <button className='button-link' onClick={onDeleteKB}>
                    Yes
                  </button>
                  <button className='button-link' onClick={() => setDeleteConfirmation(false)}>
                    No
                  </button>
                </div>
              </React.Fragment>
            )}
          </div>
        )}
        {updating && (
          <div className='modal-footer justify-content-center'>
            <img className='p-2' src={loader} />
          </div>
        )}
      </Modal>
    </React.Fragment>
  )
}

export default SettingsModal
